import { FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { ComposableDeviceValues } from '../AddComposableDevice';
import { ErrorText } from '../../../../../theme/components/Forms';
import {
  StyledAutoComplete,
  StyledInputLabel,
  StyledStack,
  StyledTextField,
} from './StyledElements';
import {
  Box,
  FormControl,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { getPolyglot } from '../../../../../i18n';
import { useAppSelector } from '../../../../../redux/store.model';
import { getMachineTypeSelector } from '../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import { getAllAvailableDevicesToAddByGroupIdSelector } from '../../../../../redux/commander/selectors';
import _ from 'lodash';
import { ST_COMPOSABLE_CONSUMPTION_DATA_KEY } from '../../../../../redux/composableDevice/ComposableDevice.model';

export interface OptimaDeviceModelType {
  name: string;
  commandersQuantity: number;
}
export const DecksForm = ({
  formik,
}: {
  formik: FormikProps<ComposableDeviceValues>;
}) => {
  const polyglot = getPolyglot();
  const theme = useTheme();

  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: formik.values.composableDevice?.machineTypeId,
    })
  );

  const businenessModelOptions = JSON.parse(
    machineType?.labels?.businessModelOptions || '[]'
  ) as OptimaDeviceModelType[];

  const powerValuesKeys = JSON.parse(
    machineType?.labels?.POWER_VALUES || '{}'
  ) as Record<number, Record<number, Record<string, number>>>;

  const businessModel = formik.values.composableDevice?.labels?.businessModel;
  const width = Number(formik.values.composableDevice?.labels?.width ?? 0);
  const depth = Number(formik.values.composableDevice?.labels?.depth ?? 0);

  const selectedModel = businenessModelOptions.find(
    (model) => model.name === businessModel
  ) || { commandersQuantity: 0, name: '' };

  const electricalDecks = formik.values.composableDevice?.labels
    ?.electricalDecks
    ? Number(formik.values.composableDevice?.labels?.electricalDecks)
    : 0;
  const burnerDecks = formik.values.composableDevice?.labels?.burnerDecks
    ? Number(formik.values.composableDevice?.labels?.burnerDecks)
    : 0;
  const commandersQuantity = Number(selectedModel.commandersQuantity) || 0;
  // Burner decks share a single commander
  const burnerCommanders = burnerDecks > 0 ? 1 : 0;
  const totalDecks =
    machineType?.name === 'OPTIMA'
      ? electricalDecks + burnerCommanders
      : commandersQuantity;
  const deckIndices =
    burnerCommanders > 0
      ? [...Array.from({ length: electricalDecks }, (_, index) => index + 1), 0]
      : Array.from({ length: totalDecks }, (_, index) => index);
  // Total number of decks (burner + electrical) used to look up burner power
  const numberOfDecks = burnerDecks + electricalDecks;
  const labels = {
    ...machineType?.labels,
    ...formik.values.composableDevice?.labels,
  };

  const isOptima = machineType?.name === 'OPTIMA';
  const electricalPowerKeys =
    JSON.parse(labels['electricalPowerKeys'] || '[]') || [];
  const burnerPowerKeys = JSON.parse(labels['burnerPowerKeys'] || '[]') || [];

  const [deviceValues, setDeviceValues] = useState<string[]>([]);
  const previousDeckConfiguration = useRef<string | null>(null);
  const steamPowerValues = deviceValues.map(
    (_, index) => Number(labels[`STEAM_POWER_${index}`]) || 0
  );

  useEffect(() => {
    const deckConfiguration = [
      commandersQuantity,
      electricalDecks,
      burnerDecks,
      totalDecks,
    ].join(':');
    const configurationChanged =
      previousDeckConfiguration.current !== null &&
      previousDeckConfiguration.current !== deckConfiguration;
    previousDeckConfiguration.current = deckConfiguration;

    if (configurationChanged) {
      const clearedDevices = Array.from({ length: totalDecks }, () => '');
      setDeviceValues(clearedDevices);
      void formik.setFieldValue('devices', clearedDevices, false);
      void formik.setFieldTouched('devices', false, false);
      formik.setFieldError('devices', undefined);
      return;
    }

    setDeviceValues((prev) => {
      const source =
        prev.length === 0 && formik.values?.devices?.length
          ? formik.values.devices
          : prev;
      if (source.length === totalDecks) {
        return source === prev ? prev : source;
      }
      return Array.from(
        { length: totalDecks },
        (_, index) => source[index] ?? ''
      );
    });
  }, [
    commandersQuantity,
    electricalDecks,
    burnerDecks,
    totalDecks,
    formik.values?.devices,
  ]);

  const devices = useAppSelector((state) =>
    getAllAvailableDevicesToAddByGroupIdSelector(state, {
      groupId: undefined,
    })
  );
  useEffect(() => {
    if (deviceValues.length > 0 && deviceValues.length === totalDecks) {
      const powerData = (powerValuesKeys[depth]?.[width] || {}) as Record<
        string,
        number
      >;
      const powerDataValues = Object.entries(powerData).reduce(
        (acc, [key, curr]) => {
          if (typeof curr !== 'number') {
            return acc;
          }
          return acc + curr / 1000; // Convert W to kW
        },
        0
      );
      const GAS_VALUES = (powerData?.['GAS_BURNER'] || {}) as Record<
        number,
        number
      >;
      const burnerValue = GAS_VALUES[numberOfDecks] || 0;
      const commanderValues = deviceValues.reduce((acc, curr, index) => {
        const isBurnerCommander = burnerCommanders > 0 && index === 0;
        return {
          ...acc,
          [curr]: isBurnerCommander
            ? burnerValue
            : powerDataValues + (isOptima ? steamPowerValues[index] / 1000 : 0),
        };
      }, {});

      void formik.setFieldValue('devices', deviceValues, false);
      formik.setFieldValue(
        `composableDevice.labels.${ST_COMPOSABLE_CONSUMPTION_DATA_KEY}`,
        JSON.stringify(commanderValues)
      );

      (async () => {
        await formik.validateField('devices');
        formik.validateForm();
      })();
    }
  }, [burnerDecks, width, depth, deviceValues, steamPowerValues.join(',')]);

  const showDecks =
    (width && depth) ||
    (businessModel === 'OPTIMA' &&
      electricalDecks + burnerDecks === commandersQuantity);

  return (
    <Stack display={'flex'} flexDirection={'column'}>
      {showDecks ? (
        <>
          {deckIndices.map((deviceIndex, displayIndex) => {
            const isBurnerDeck = burnerCommanders > 0 && deviceIndex === 0;
            return (
              <StyledStack
                key={`deck-${deviceIndex}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isBurnerDeck
                    ? '4fr 1fr'
                    : isOptima
                    ? 'repeat(5, 1fr)'
                    : 'repeat(4, 1fr)',
                }}
              >
                <FormControl>
                  <StyledInputLabel
                    htmlFor={`devices-select-deck-${deviceIndex}`}
                  >
                    {isBurnerDeck
                      ? burnerDecks === 1
                        ? polyglot.t('fours_fringand.oven.shelf', {
                            number: 1,
                          })
                        : polyglot.t('fours_fringand.oven.burnerShelf', {
                            levels: `1-${burnerDecks}`,
                          })
                      : polyglot.t('fours_fringand.oven.shelf', {
                          number: commandersQuantity - displayIndex,
                        })}
                  </StyledInputLabel>
                  <StyledAutoComplete
                    id={`devices-select-deck-${deviceIndex}`}
                    options={devices}
                    autoHighlight
                    getOptionLabel={(option: any) => option.name || ''}
                    value={
                      devices.find(
                        (d) => deviceValues[deviceIndex] === d.uuid
                      ) || null
                    }
                    onChange={(_, value: any) => {
                      setDeviceValues((prevValues) => {
                        const newValues = [...prevValues];
                        newValues[deviceIndex] = value?.uuid || '';
                        return newValues;
                      });
                      void formik.setFieldTouched('devices', true, false);
                    }}
                    onBlur={() => void formik.setFieldTouched('devices', true)}
                    renderOption={(props: any, option: any) => {
                      const { key, ...optionProps } = props;
                      return (
                        <Box key={key} component="li" {...optionProps}>
                          {option.name || ''}
                        </Box>
                      );
                    }}
                    renderInput={(params) => <StyledTextField {...params} />}
                  />
                </FormControl>
                {isBurnerDeck
                  ? Array.from({ length: +burnerPowerKeys.length }).map(
                      (_, burnerPowerKeysIndex) => {
                        const key = burnerPowerKeys[burnerPowerKeysIndex];
                        const powerValue = (powerValuesKeys[depth]?.[width] ||
                          {}) as Record<string, number>;
                        const GAS_VALUES = (powerValue?.['GAS_BURNER'] ||
                          {}) as Record<number, number>;
                        const value = GAS_VALUES[numberOfDecks] || 0;
                        return (
                          <FormControl
                            key={`burner-power-key-${burnerPowerKeysIndex}`}
                          >
                            <StyledInputLabel
                              htmlFor={`power-value-input-${burnerPowerKeysIndex}`}
                            >
                              {polyglot.t(`composable_device.${key}`)}
                            </StyledInputLabel>
                            <StyledTextField
                              id={`power-value-input-${burnerPowerKeysIndex}`}
                              name={`powerValue-${burnerPowerKeysIndex}`}
                              type="number"
                              inputProps={{ min: 0, placeholder: '0' }}
                              value={value !== undefined ? value : ''}
                              InputProps={{
                                disabled: true,
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    sx={{
                                      maxHeight: '100%',
                                      height: '100%',
                                      margin: 0,
                                      backgroundColor:
                                        theme.palette.common.white,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        display: 'flex',
                                        height: '100%',
                                        alignItems: 'center',
                                        backgroundColor:
                                          theme.palette.common.white,
                                        margin: '0 0.5rem',
                                      }}
                                      variant="body2"
                                    >
                                      kW
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </FormControl>
                        );
                      }
                    )
                  : Array.from({ length: +electricalPowerKeys.length }).map(
                      (_, electricalPowerKeysIndex) => {
                        const key =
                          electricalPowerKeys[electricalPowerKeysIndex];
                        const powerValue = (powerValuesKeys[depth]?.[width] ||
                          {}) as Record<string, number>;

                        return (
                          <FormControl key={electricalPowerKeysIndex}>
                            <StyledInputLabel
                              htmlFor={`power-value-input-${electricalPowerKeysIndex}`}
                            >
                              {polyglot.t(`composable_device.${key}`)}
                            </StyledInputLabel>
                            <StyledTextField
                              id={`power-value-input-${electricalPowerKeysIndex}`}
                              name={`powerValue-${electricalPowerKeysIndex}`}
                              type="number"
                              value={
                                powerValue?.[key] !== undefined
                                  ? powerValue[key]
                                  : ''
                              }
                              inputProps={{ min: 0, placeholder: '0' }}
                              InputProps={{
                                disabled: true,
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    sx={{
                                      maxHeight: '100%',
                                      height: '100%',
                                      margin: 0,
                                      backgroundColor:
                                        theme.palette.common.white,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        display: 'flex',
                                        height: '100%',
                                        alignItems: 'center',
                                        backgroundColor:
                                          theme.palette.common.white,
                                        margin: '0 0.5rem',
                                      }}
                                      variant="body2"
                                    >
                                      W
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </FormControl>
                        );
                      }
                    )}
              </StyledStack>
            );
          })}
          {formik.errors.devices && formik.touched.devices && (
            <ErrorText fontSize={'0.9rem'} sx={{ margin: '-1rem 0 1rem 0' }}>
              {formik.errors.devices}
            </ErrorText>
          )}
        </>
      ) : null}

      <StyledStack>
        <FormControl>
          <StyledInputLabel htmlFor={`kw-value`}>
            {polyglot.t(
              'composable_device.dashboard_consumption_setting_kwh_price'
            )}
          </StyledInputLabel>
          <StyledTextField
            id={`kw-value`}
            name={`kw-value`}
            type="number"
            inputProps={{ min: 0, step: 0.1 }}
            onChange={(e) => {
              formik.setFieldValue(
                'composableDevice.labels.ST_KILO_WATT_EUR_CONSUMPTION',
                e.target.value
              );
            }}
            value={
              formik.values.composableDevice?.labels
                ?.ST_KILO_WATT_EUR_CONSUMPTION || ''
            }
            InputProps={{
              placeholder: '1,24',
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    maxHeight: '100%',
                    height: '100%',
                    margin: 0,
                    backgroundColor: theme.palette.common.white,
                  }}
                >
                  <Typography
                    sx={{
                      display: 'flex',
                      height: '100%',
                      alignItems: 'center',
                      backgroundColor: theme.palette.common.white,
                      margin: '0 0.5rem',
                    }}
                    variant="body2"
                  >
                    €
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
      </StyledStack>
    </Stack>
  );
};
